import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { 
  Dialog, DialogContent, DialogDescription, DialogFooter, 
  DialogHeader, DialogTitle 
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  ArrowLeft, User, Building, Phone, Mail, Calendar, CheckCircle,
  Clock, AlertCircle, FileText, Briefcase, Receipt, Loader2,
  ExternalLink, Shield, XCircle, Download, PoundSterling, KeyRound,
  Copy, Link as LinkIcon
} from "lucide-react";
import { QRCodeSVG } from "qrcode.react";

const API = process.env.REACT_APP_BACKEND_URL;

const statusColors = {
  invited: "bg-blue-100 text-blue-700",
  onboarding: "bg-purple-100 text-purple-700",
  pending_approval: "bg-amber-100 text-amber-700",
  active: "bg-green-100 text-green-700",
  rejected: "bg-red-100 text-red-700",
  suspended: "bg-slate-100 text-slate-700"
};

const statusLabels = {
  invited: "Invited",
  onboarding: "Onboarding",
  pending_approval: "Pending Approval",
  active: "Active",
  rejected: "Rejected",
  suspended: "Suspended"
};

// Invoice status colors
const invoiceStatusColors = {
  submitted: "bg-blue-100 text-blue-700",
  approved: "bg-green-100 text-green-700",
  paid: "bg-emerald-100 text-emerald-700",
  rejected: "bg-red-100 text-red-700"
};

// InvoicesTab Component for admin invoice management
const InvoicesTab = ({ contractorId, token, onRefresh }) => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totals, setTotals] = useState({});
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showPayModal, setShowPayModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [paymentDate, setPaymentDate] = useState("");
  const [paymentRef, setPaymentRef] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchInvoices();
  }, [contractorId]);

  const fetchInvoices = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/api/admin/contractor-invoices/${contractorId}`, { headers });
      setInvoices(response.data.invoices || []);
      setTotals(response.data.totals || {});
    } catch (error) {
      console.error("Error fetching invoices:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (invoice) => {
    setSubmitting(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`${API}/api/admin/contractor-invoices/${invoice.invoice_id}/approve`, {}, { headers });
      toast.success("Invoice approved!");
      fetchInvoices();
      onRefresh?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to approve invoice");
    } finally {
      setSubmitting(false);
    }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) {
      toast.error("Please provide a rejection reason");
      return;
    }
    setSubmitting(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(
        `${API}/api/admin/contractor-invoices/${selectedInvoice.invoice_id}/reject`,
        { reason: rejectReason },
        { headers }
      );
      toast.success("Invoice rejected");
      setShowRejectModal(false);
      setRejectReason("");
      fetchInvoices();
      onRefresh?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reject invoice");
    } finally {
      setSubmitting(false);
    }
  };

  const handleMarkPaid = async () => {
    setSubmitting(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(
        `${API}/api/admin/contractor-invoices/${selectedInvoice.invoice_id}/mark-paid`,
        { payment_date: paymentDate || new Date().toISOString(), payment_reference: paymentRef },
        { headers }
      );
      toast.success("Invoice marked as paid!");
      setShowPayModal(false);
      setPaymentDate("");
      setPaymentRef("");
      fetchInvoices();
      onRefresh?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to mark as paid");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
      </div>
    );
  }

  const submitted = invoices.filter(i => i.status === "submitted");
  const approved = invoices.filter(i => i.status === "approved");
  const paid = invoices.filter(i => i.status === "paid");
  const rejected = invoices.filter(i => i.status === "rejected");

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6 text-center">
            <div className="text-2xl font-bold text-blue-600">{submitted.length}</div>
            <div className="text-sm text-muted-foreground">Submitted</div>
            <div className="text-xs text-slate-500">£{(totals.submitted_amount || 0).toFixed(2)}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6 text-center">
            <div className="text-2xl font-bold text-green-600">{approved.length}</div>
            <div className="text-sm text-muted-foreground">Approved</div>
            <div className="text-xs text-slate-500">£{(totals.approved_amount || 0).toFixed(2)}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6 text-center">
            <div className="text-2xl font-bold text-emerald-600">{paid.length}</div>
            <div className="text-sm text-muted-foreground">Paid</div>
            <div className="text-xs text-slate-500">£{(totals.paid_amount || 0).toFixed(2)}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6 text-center">
            <div className="text-2xl font-bold text-red-600">{rejected.length}</div>
            <div className="text-sm text-muted-foreground">Rejected</div>
          </CardContent>
        </Card>
      </div>

      {/* Submitted Invoices - Need Action */}
      {submitted.length > 0 && (
        <Card className="border-blue-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-blue-600" />
              Pending Review ({submitted.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {submitted.map(inv => (
                <div key={inv.invoice_id} className="flex items-center justify-between p-3 bg-blue-50 border border-blue-100 rounded-lg">
                  <div>
                    <p className="font-medium">Job #{inv.job_id?.slice(-6)}</p>
                    <p className="text-sm text-slate-600">
                      £{inv.amount?.toFixed(2)} • {new Date(inv.submitted_at).toLocaleDateString()}
                    </p>
                    {inv.invoice_number && (
                      <p className="text-xs text-slate-500">Inv #{inv.invoice_number}</p>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-red-300 text-red-600 hover:bg-red-50"
                      onClick={() => { setSelectedInvoice(inv); setShowRejectModal(true); }}
                    >
                      Reject
                    </Button>
                    <Button
                      size="sm"
                      className="bg-green-600 hover:bg-green-700"
                      onClick={() => handleApprove(inv)}
                      disabled={submitting}
                    >
                      Approve
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Approved Invoices - Ready to Pay */}
      {approved.length > 0 && (
        <Card className="border-green-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-600" />
              Approved - Awaiting Payment ({approved.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {approved.map(inv => (
                <div key={inv.invoice_id} className="flex items-center justify-between p-3 bg-green-50 border border-green-100 rounded-lg">
                  <div>
                    <p className="font-medium">Job #{inv.job_id?.slice(-6)}</p>
                    <p className="text-sm text-slate-600">
                      £{inv.amount?.toFixed(2)} • Approved {inv.approved_at ? new Date(inv.approved_at).toLocaleDateString() : ""}
                    </p>
                  </div>
                  <Button
                    size="sm"
                    className="bg-emerald-600 hover:bg-emerald-700"
                    onClick={() => { setSelectedInvoice(inv); setShowPayModal(true); }}
                  >
                    Mark as Paid
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* All Invoices List */}
      <Card>
        <CardHeader>
          <CardTitle>All Invoices</CardTitle>
        </CardHeader>
        <CardContent>
          {invoices.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <Receipt className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>No invoices submitted yet</p>
            </div>
          ) : (
            <div className="divide-y">
              {invoices.map(inv => (
                <div key={inv.invoice_id} className="py-3 flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <Badge className={invoiceStatusColors[inv.status] || "bg-slate-100"}>
                        {inv.status}
                      </Badge>
                      <span className="font-medium">Job #{inv.job_id?.slice(-6)}</span>
                    </div>
                    <p className="text-sm text-slate-500">
                      {inv.submitted_at ? new Date(inv.submitted_at).toLocaleDateString() : "-"}
                      {inv.invoice_number && ` • Inv #${inv.invoice_number}`}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-medium">£{inv.amount?.toFixed(2)}</p>
                    {inv.paid_at && (
                      <p className="text-xs text-emerald-600">Paid {new Date(inv.paid_at).toLocaleDateString()}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Reject Modal */}
      <Dialog open={showRejectModal} onOpenChange={setShowRejectModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject Invoice</DialogTitle>
            <DialogDescription>
              The contractor will be notified and can re-upload a corrected invoice.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <label className="text-sm font-medium">Rejection Reason *</label>
            <Textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Please explain why this invoice is being rejected..."
              className="mt-2"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRejectModal(false)}>Cancel</Button>
            <Button
              onClick={handleReject}
              disabled={submitting || !rejectReason.trim()}
              className="bg-red-600 hover:bg-red-700"
            >
              {submitting && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              Reject Invoice
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Mark Paid Modal */}
      <Dialog open={showPayModal} onOpenChange={setShowPayModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Mark Invoice as Paid</DialogTitle>
            <DialogDescription>
              Invoice amount: £{selectedInvoice?.amount?.toFixed(2)}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div>
              <label className="text-sm font-medium">Payment Date</label>
              <Input
                type="date"
                value={paymentDate}
                onChange={(e) => setPaymentDate(e.target.value)}
                className="mt-1"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Payment Reference (optional)</label>
              <Input
                value={paymentRef}
                onChange={(e) => setPaymentRef(e.target.value)}
                placeholder="e.g., BACS ref, cheque number..."
                className="mt-1"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPayModal(false)}>Cancel</Button>
            <Button
              onClick={handleMarkPaid}
              disabled={submitting}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              {submitting && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              Mark as Paid
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export const ContractorDetailsPage = () => {
  const { contractorId } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem("token");

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [approvalAction, setApprovalAction] = useState(null);
  const [approvalNotes, setApprovalNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sendingInvite, setSendingInvite] = useState(false);
  const [portalStatus, setPortalStatus] = useState(null);

  useEffect(() => {
    fetchContractorDetails();
    fetchPortalStatus();
  }, [contractorId]);

  const fetchContractorDetails = async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/api/contractor-management/${contractorId}/details`, { headers });
      setData(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to load contractor details");
      navigate("/admin/contractors");
    } finally {
      setLoading(false);
    }
  };

  const fetchPortalStatus = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/api/contractor-management/${contractorId}/portal-status`, { headers });
      setPortalStatus(response.data);
    } catch (error) {
      console.error("Failed to fetch portal status:", error);
    }
  };

  // Result modal for Send Invite / Reset Password — surfaces the invite link
  // and/or temporary password so admins can copy them while emails are off.
  const [credentialResult, setCredentialResult] = useState(null);
  // { kind: 'invite'|'reset', email, invite_link?, temp_password?, email_sent }
  
  const copyToClipboard = (text, label) => {
    navigator.clipboard?.writeText(text || '');
    toast.success(`${label} copied`);
  };
  
  const handleSendInvite = async () => {
    setSendingInvite(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.post(`${API}/api/contractor-management/${contractorId}/resend-invite`, {}, { headers });
      const emailSent = !!response.data?.notifications?.email_sent;
      toast.success(emailSent ? "Portal invite sent successfully!" : "Invite link generated — share it manually below");
      setCredentialResult({
        kind: 'invite',
        email: contractor?.email,
        invite_link: response.data?.invite_link,
        email_sent: emailSent,
      });
      fetchPortalStatus();
      fetchContractorDetails();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send invite");
    } finally {
      setSendingInvite(false);
    }
  };

  const [sendingSMS, setSendingSMS] = useState(false);
  
  const handleSendInviteSMS = async () => {
    setSendingSMS(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.post(`${API}/api/contractor-management/${contractorId}/send-invite-sms`, {}, { headers });
      toast.success(response.data.message || "SMS invite sent!");
      fetchPortalStatus();
      fetchContractorDetails();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send SMS");
    } finally {
      setSendingSMS(false);
    }
  };

  const [resettingPassword, setResettingPassword] = useState(false);
  
  const handleResetPassword = async () => {
    if (!confirm("Reset this contractor's password? A new temporary password will be generated.")) {
      return;
    }
    setResettingPassword(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.post(`${API}/api/contractor-management/${contractorId}/reset-password`, {}, { headers });
      const emailSent = !!response.data?.email_sent;
      toast.success(emailSent
        ? "Password reset email sent to contractor!"
        : "Password reset — share the new password manually below");
      setCredentialResult({
        kind: 'reset',
        email: response.data?.email || contractor?.email,
        temp_password: response.data?.temp_password,
        email_sent: emailSent,
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reset password");
    } finally {
      setResettingPassword(false);
    }
  };

  const handleApproval = async () => {
    setSubmitting(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`${API}/api/contractor-management/${contractorId}/approve`, {
        action: approvalAction,
        notes: approvalNotes
      }, { headers });

      toast.success(approvalAction === "approve" ? "Contractor approved!" : "Contractor rejected");
      setShowApprovalModal(false);
      setApprovalNotes("");
      fetchContractorDetails();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Action failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!data) return null;

  const { contractor, user_account, jobs, invoices } = data;
  const status = contractor?.status || "active";
  const insuranceDocs = contractor?.insurance_documents || [];
  const latestInsurance = insuranceDocs[insuranceDocs.length - 1];

  return (
    <div className="min-h-screen bg-slate-50 p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Button variant="ghost" onClick={() => navigate(-1)}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <div className="flex-1">
            <h1 className="text-2xl font-bold text-slate-800">{contractor?.name}</h1>
            <p className="text-slate-500">{contractor?.company || "Independent Contractor"}</p>
          </div>
          <Badge className={statusColors[status] || "bg-slate-100"}>
            {statusLabels[status] || status}
          </Badge>
        </div>

        {/* Pending Approval Alert */}
        {status === "pending_approval" && (
          <Card className="mb-6 border-amber-200 bg-amber-50">
            <CardContent className="py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Clock className="w-5 h-5 text-amber-600" />
                  <div>
                    <p className="font-medium text-amber-800">Pending Insurance Approval</p>
                    <p className="text-sm text-amber-600">Review the insurance documents and approve or reject</p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    className="border-red-300 text-red-600 hover:bg-red-50"
                    onClick={() => { setApprovalAction("reject"); setShowApprovalModal(true); }}
                  >
                    <XCircle className="w-4 h-4 mr-2" />
                    Reject
                  </Button>
                  <Button 
                    className="bg-green-600 hover:bg-green-700"
                    onClick={() => { setApprovalAction("approve"); setShowApprovalModal(true); }}
                  >
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Approve
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        <Tabs defaultValue="profile" className="space-y-6">
          <TabsList>
            <TabsTrigger value="profile">Profile</TabsTrigger>
            <TabsTrigger value="insurance">Insurance</TabsTrigger>
            <TabsTrigger value="jobs">Jobs ({jobs?.total_count || 0})</TabsTrigger>
            <TabsTrigger value="invoices">Invoices ({invoices?.total_count || 0})</TabsTrigger>
          </TabsList>

          {/* Profile Tab */}
          <TabsContent value="profile">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <User className="w-5 h-5" />
                    Contact Details
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center gap-3">
                    <Mail className="w-4 h-4 text-slate-400" />
                    <span>{contractor?.email || "No email"}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Phone className="w-4 h-4 text-slate-400" />
                    <span>{contractor?.phone || "No phone"}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Building className="w-4 h-4 text-slate-400" />
                    <span>{contractor?.company || "No company"}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Briefcase className="w-4 h-4 text-slate-400" />
                    <span className="capitalize">{contractor?.specialty || "General"}</span>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Calendar className="w-5 h-5" />
                    Account Info
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex justify-between">
                    <span className="text-slate-500">Status</span>
                    <Badge className={statusColors[status]}>
                      {statusLabels[status] || status}
                    </Badge>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Invited</span>
                    <span>{contractor?.invite_sent_at ? new Date(contractor.invite_sent_at).toLocaleDateString() : "-"}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Joined</span>
                    <span>{contractor?.invite_accepted_at ? new Date(contractor.invite_accepted_at).toLocaleDateString() : "-"}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Approved</span>
                    <span>{contractor?.approved_at ? new Date(contractor.approved_at).toLocaleDateString() : "-"}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-slate-500">Portal Access</span>
                    <div className="flex items-center gap-2">
                      {portalStatus?.has_portal_access ? (
                        <Badge className="bg-green-100 text-green-700">
                          <CheckCircle className="w-3 h-3 mr-1" />
                          Active
                        </Badge>
                      ) : portalStatus?.invite_pending ? (
                        <Badge className="bg-amber-100 text-amber-700">
                          <Clock className="w-3 h-3 mr-1" />
                          Invite Pending
                        </Badge>
                      ) : (
                        <Badge className="bg-slate-100 text-slate-700">
                          No Access
                        </Badge>
                      )}
                    </div>
                  </div>
                  {/* Send/Resend Invite Button */}
                  {contractor?.email && !portalStatus?.has_portal_access && (
                    <div className="pt-3 border-t space-y-2">
                      <Button
                        onClick={handleSendInvite}
                        disabled={sendingInvite}
                        className="w-full bg-blue-600 hover:bg-blue-700"
                        data-testid="send-portal-invite-btn"
                      >
                        {sendingInvite ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Sending...
                          </>
                        ) : portalStatus?.invite_pending ? (
                          <>
                            <Mail className="w-4 h-4 mr-2" />
                            Resend Invite (Email)
                          </>
                        ) : (
                          <>
                            <Mail className="w-4 h-4 mr-2" />
                            Send Invite (Email)
                          </>
                        )}
                      </Button>
                      {contractor?.phone && (
                        <Button
                          onClick={handleSendInviteSMS}
                          disabled={sendingSMS}
                          variant="outline"
                          className="w-full border-green-300 text-green-700 hover:bg-green-50"
                          data-testid="send-portal-invite-sms-btn"
                        >
                          {sendingSMS ? (
                            <>
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              Sending SMS...
                            </>
                          ) : (
                            <>
                              <Phone className="w-4 h-4 mr-2" />
                              Send Invite (SMS)
                            </>
                          )}
                        </Button>
                      )}
                    </div>
                  )}
                  {/* Reset Password Button - only show if contractor HAS portal access */}
                  {contractor?.email && portalStatus?.has_portal_access && (
                    <div className="pt-3 border-t">
                      <Button
                        onClick={handleResetPassword}
                        disabled={resettingPassword}
                        variant="outline"
                        className="w-full border-orange-300 text-orange-700 hover:bg-orange-50"
                        data-testid="reset-password-btn"
                      >
                        {resettingPassword ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Resetting...
                          </>
                        ) : (
                          <>
                            <KeyRound className="w-4 h-4 mr-2" />
                            Reset Password
                          </>
                        )}
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Insurance Tab */}
          <TabsContent value="insurance">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="w-5 h-5" />
                  Insurance Documents
                </CardTitle>
                <CardDescription>
                  Public Liability Insurance documentation
                </CardDescription>
              </CardHeader>
              <CardContent>
                {insuranceDocs.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <Shield className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    <p>No insurance documents uploaded</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {insuranceDocs.map((doc, idx) => (
                      <div key={doc.document_id || idx} className="border rounded-lg p-4">
                        <div className="flex items-start justify-between">
                          <div className="space-y-2">
                            <div className="flex items-center gap-2">
                              <FileText className="w-4 h-4 text-blue-600" />
                              <span className="font-medium">{doc.file_name || "Insurance Document"}</span>
                              <Badge className={
                                doc.approval_status === "approved" ? "bg-green-100 text-green-700" :
                                doc.approval_status === "rejected" ? "bg-red-100 text-red-700" :
                                "bg-amber-100 text-amber-700"
                              }>
                                {doc.approval_status || "Pending"}
                              </Badge>
                            </div>
                            <div className="text-sm text-slate-500 space-y-1">
                              <p><strong>Insurer:</strong> {doc.insurer_name}</p>
                              <p><strong>Policy #:</strong> {doc.policy_number}</p>
                              <p><strong>Expiry:</strong> {doc.expiry_date ? new Date(doc.expiry_date).toLocaleDateString() : "-"}</p>
                              <p><strong>Uploaded:</strong> {doc.uploaded_at ? new Date(doc.uploaded_at).toLocaleDateString() : "-"}</p>
                            </div>
                            {doc.approval_notes && (
                              <p className="text-sm text-red-600 mt-2">
                                <strong>Notes:</strong> {doc.approval_notes}
                              </p>
                            )}
                          </div>
                          {doc.file_path && (
                            <Button variant="outline" size="sm">
                              <Download className="w-4 h-4 mr-2" />
                              Download
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Approval Actions for Pending */}
                {status === "pending_approval" && (
                  <div className="flex gap-2 mt-6 pt-4 border-t">
                    <Button 
                      variant="outline" 
                      className="border-red-300 text-red-600 hover:bg-red-50"
                      onClick={() => { setApprovalAction("reject"); setShowApprovalModal(true); }}
                    >
                      <XCircle className="w-4 h-4 mr-2" />
                      Reject Insurance
                    </Button>
                    <Button 
                      className="bg-green-600 hover:bg-green-700"
                      onClick={() => { setApprovalAction("approve"); setShowApprovalModal(true); }}
                    >
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Approve Insurance
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Jobs Tab */}
          <TabsContent value="jobs">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <Card>
                <CardContent className="pt-6">
                  <div className="text-center">
                    <p className="text-3xl font-bold text-amber-600">{jobs?.pending?.length || 0}</p>
                    <p className="text-sm text-slate-500">Pending Jobs</p>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="text-center">
                    <p className="text-3xl font-bold text-blue-600">{jobs?.outstanding?.length || 0}</p>
                    <p className="text-sm text-slate-500">In Progress</p>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="text-center">
                    <p className="text-3xl font-bold text-green-600">{jobs?.completed_count || 0}</p>
                    <p className="text-sm text-slate-500">Completed</p>
                  </div>
                </CardContent>
              </Card>
            </div>

            <Card>
              <CardHeader>
                <CardTitle>Recent Jobs</CardTitle>
              </CardHeader>
              <CardContent>
                {jobs?.recent?.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <Briefcase className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    <p>No jobs assigned yet</p>
                  </div>
                ) : (
                  <div className="divide-y">
                    {jobs?.recent?.map((job) => (
                      <div key={job.job_id} className="py-3 flex items-center justify-between">
                        <div>
                          <p className="font-medium">{job.title}</p>
                          <p className="text-sm text-slate-500">{job.property_address || "No address"}</p>
                        </div>
                        <div className="flex items-center gap-3">
                          <Badge className={
                            job.status === "completed" ? "bg-green-100 text-green-700" :
                            job.status === "in_progress" ? "bg-blue-100 text-blue-700" :
                            job.status === "pending" ? "bg-amber-100 text-amber-700" :
                            "bg-slate-100 text-slate-700"
                          }>
                            {job.status}
                          </Badge>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => navigate(`/admin/maintenance/${job.job_id}`)}
                          >
                            <ExternalLink className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Invoices Tab */}
          <TabsContent value="invoices">
            <InvoicesTab 
              contractorId={contractorId}
              token={token}
              onRefresh={fetchContractorDetails}
            />
          </TabsContent>
        </Tabs>

        {/* Approval Modal */}
        <Dialog open={showApprovalModal} onOpenChange={setShowApprovalModal}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {approvalAction === "approve" ? "Approve Contractor" : "Reject Contractor"}
              </DialogTitle>
              <DialogDescription>
                {approvalAction === "approve" 
                  ? "This will activate the contractor's account and allow them to receive job assignments."
                  : "The contractor will need to re-upload their documents."}
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <label className="text-sm font-medium">Notes {approvalAction === "reject" && "(required)"}</label>
              <Textarea
                value={approvalNotes}
                onChange={(e) => setApprovalNotes(e.target.value)}
                placeholder={approvalAction === "reject" ? "Reason for rejection..." : "Optional notes..."}
                className="mt-2"
              />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowApprovalModal(false)}>Cancel</Button>
              <Button 
                onClick={handleApproval}
                disabled={submitting || (approvalAction === "reject" && !approvalNotes)}
                className={approvalAction === "approve" ? "bg-green-600 hover:bg-green-700" : "bg-red-600 hover:bg-red-700"}
              >
                {submitting && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                {approvalAction === "approve" ? "Approve" : "Reject"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Credential Result Dialog — shows invite link or temp password so an
            admin can copy & manually share while emails are disabled. */}
        <Dialog open={!!credentialResult} onOpenChange={(open) => !open && setCredentialResult(null)}>
          <DialogContent className="max-w-md" data-testid="credential-result-modal">
            {credentialResult && (
              <>
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    {credentialResult.kind === 'invite' ? (
                      <LinkIcon className="w-5 h-5 text-blue-600" />
                    ) : (
                      <KeyRound className="w-5 h-5 text-amber-600" />
                    )}
                    {credentialResult.kind === 'invite' ? 'Portal Invite Link' : 'Temporary Password'}
                  </DialogTitle>
                  <DialogDescription>
                    {credentialResult.email_sent
                      ? `Sent to ${credentialResult.email}. You can also copy below to share another way.`
                      : `Email sending is currently disabled. Copy the details below and share them with ${credentialResult.email || 'the contractor'} via your preferred channel.`}
                  </DialogDescription>
                </DialogHeader>

                <div className="space-y-3 py-2">
                  {credentialResult.email && (
                    <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                      <p className="text-xs uppercase tracking-wide text-slate-500 mb-1">Email</p>
                      <div className="flex items-center justify-between gap-2">
                        <code className="text-sm font-medium text-slate-900 break-all">{credentialResult.email}</code>
                        <Button size="sm" variant="ghost" className="shrink-0"
                          onClick={() => copyToClipboard(credentialResult.email, 'Email')}>
                          <Copy className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </div>
                  )}

                  {credentialResult.kind === 'invite' && credentialResult.invite_link && (
                    <div className="rounded-lg border border-blue-200 bg-blue-50 p-3">
                      <p className="text-xs uppercase tracking-wide text-blue-700 mb-2">Onboarding Link (expires in 7 days)</p>
                      <div className="flex items-start gap-3">
                        <div className="shrink-0 rounded-md bg-white p-1.5 border border-blue-200">
                          <QRCodeSVG value={credentialResult.invite_link} size={84} level="M" includeMargin={false} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <code className="text-xs font-medium text-slate-900 break-all flex-1" data-testid="invite-link-value">
                              {credentialResult.invite_link}
                            </code>
                            <Button size="sm" variant="ghost" className="shrink-0 -mt-1"
                              onClick={() => copyToClipboard(credentialResult.invite_link, 'Link')}
                              data-testid="copy-invite-link">
                              <Copy className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                          <p className="text-[11px] text-blue-700/80 mt-2">Scan the QR with a phone camera to open instantly.</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {credentialResult.kind === 'reset' && credentialResult.temp_password && (
                    <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
                      <p className="text-xs uppercase tracking-wide text-amber-700 mb-1">Temporary Password</p>
                      <div className="flex items-center justify-between gap-2">
                        <code className="text-sm font-bold text-slate-900 break-all" data-testid="reset-temp-password">
                          {credentialResult.temp_password}
                        </code>
                        <Button size="sm" variant="ghost" className="shrink-0"
                          onClick={() => copyToClipboard(credentialResult.temp_password, 'Password')}
                          data-testid="copy-temp-password">
                          <Copy className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                      <p className="text-xs text-amber-700 mt-2">
                        The contractor should change this password on first login.
                      </p>
                    </div>
                  )}

                  {!credentialResult.invite_link && !credentialResult.temp_password && (
                    <div className="rounded-lg border border-red-200 bg-red-50 p-3">
                      <p className="text-sm text-red-800">
                        The server didn't return the credential. If this just happened on production, the backend may not be on the latest build — try redeploying.
                      </p>
                    </div>
                  )}

                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={() => {
                      const lines = ['Hi,', '', 'Your Goodacres Contractor Portal access:'];
                      if (credentialResult.email) lines.push(`Email: ${credentialResult.email}`);
                      if (credentialResult.temp_password) lines.push(`Temporary password: ${credentialResult.temp_password}`);
                      if (credentialResult.invite_link) lines.push(`Set up your account here: ${credentialResult.invite_link}`);
                      lines.push('', 'Goodacres Team');
                      copyToClipboard(lines.join('\n'), 'Message');
                    }}
                    data-testid="copy-credential-message"
                  >
                    <Copy className="w-4 h-4 mr-2" />
                    Copy everything as a message
                  </Button>
                </div>

                <DialogFooter>
                  <Button onClick={() => setCredentialResult(null)} data-testid="credential-result-close">
                    Done
                  </Button>
                </DialogFooter>
              </>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default ContractorDetailsPage;
